/**
 * Helpers for building chat-route URLs that launch focused drills.
 *
 * Every drill-launching helper populates `drill_target` (canonical
 * skill id or `"variety"`) and `drill_difficulty` URL params. The
 * backend composes the LLM-facing addendum prose server-side from
 * these structured inputs — clients never hand-author LLM prose.
 *
 * `mode` is the session-level shape — `spot_drill` / `archetype_drill`
 * / `switching_framework`. The helpers here pick the mode automatically
 * from the drill target via `modeForSkill`. Getting this right matters
 * because (a) the mode drives a different mode-prose layer in the
 * composed addendum (e.g. archetype mode tells the LLM to weight
 * signals toward archetype_reads / exploit_calibration), and (b) the
 * library lookup will eventually key on (drill_target, mode, difficulty)
 * — mis-tagged spots fragment the pool.
 *
 * See `backend/app/mode_addendums.py` for the prose composition.
 */

import type {
  DrillChoiceOption,
  DrillDifficulty,
  LeakPattern,
  RecommendedDrill,
  SessionMode,
  SkillState,
} from "@/shared/models";

// Spot-producing modes land on the focused single-spot DrillView; non-drill
// conversational modes (Just Ask, Concept Deep Dive) open the scrolling
// chat thread.
const NEW_DRILL_PATH = "/drill/new";
const NEW_CHAT_PATH = "/chat/new";

// Conversational modes have no constructed spot to focus on — Concept Deep
// Dive explains a framework and ends each turn with a "where next?" choices
// block; Just Ask is free-form. They stay on the chat thread.
const CONVERSATIONAL_MODES: ReadonlySet<SessionMode> = new Set([
  "concept_deep_dive",
  "just_ask",
  "session_warmup",
  "session_debrief",
  "session_consult",
]);

/**
 * Launch path for a raw session mode. `DrillSelector` is the one launcher
 * that handles every mode (including conversational ones), so the
 * spot-vs-chat decision lives here in one place. The drill-target helpers
 * below always target `/drill/new` directly since they're always drills.
 */
export function drillPathForMode(mode: SessionMode): string {
  return CONVERSATIONAL_MODES.has(mode) ? NEW_CHAT_PATH : NEW_DRILL_PATH;
}

// Skills that exercise per-archetype exploitation or the meta-skills
// of archetype identification + exploit calibration. These belong in
// `archetype_drill` mode where each spot is constructed against a
// tagged opponent and the addendum weights signals toward
// archetype_reads / exploit_calibration.
const ARCHETYPE_SKILLS: ReadonlySet<string> = new Set([
  "station_play",
  "nit_play",
  "maniac_play",
  "limper_whale_play",
  "tilted_player_play",
  "archetype_reads",
  "exploit_calibration",
]);

// Skills whose central decision is a read-shift moment — they belong
// in `switching_framework` mode.
const SWITCHING_SKILLS: ReadonlySet<string> = new Set([
  "switching_framework",
]);

// The 24 canonical skill ids — MIRROR of backend `taxonomy.VALID_SKILL_IDS`
// (read verbatim from app/skills/taxonomy.py). Used to fail-closed when the
// coach emits a drill-launch choice: a target NOT in this set degrades to a
// plain re-send button rather than routing to a /drill/new that the backend
// would 422. `"variety"` is intentionally excluded — a coach never says
// "drill variety to fix a leak." Kept in sync via chatRoute.test.ts (asserts
// size === 24 + that modeForSkill resolves every id), same discipline as
// ARCHETYPE_SKILLS / POSITIONS_CLOCKWISE.
export const VALID_DRILL_TARGETS: ReadonlySet<string> = new Set([
  "preflop_opens",
  "preflop_3betting",
  "preflop_4betting",
  "bb_defense",
  "flop_cbet_choice",
  "flop_cbet_sizing",
  "flop_facing_cbet",
  "range_vs_nut_advantage",
  "turn_barreling",
  "turn_defending",
  "river_value_betting",
  "river_bluff_selection",
  "river_bluff_catching",
  "threebet_pots",
  "facing_aggression",
  "archetype_reads",
  "exploit_calibration",
  "switching_framework",
  "gto_baseline",
  "station_play",
  "nit_play",
  "maniac_play",
  "limper_whale_play",
  "tilted_player_play",
]);

/**
 * Map a skill id (or the "variety" sentinel) to the appropriate
 * session mode. Fundamentals + variety fall through to `spot_drill`.
 *
 * Exported for testing — the mapping is the load-bearing piece of this
 * file and we want to pin it against regression for every known skill.
 */
export function modeForSkill(skillId: string): SessionMode {
  if (ARCHETYPE_SKILLS.has(skillId)) return "archetype_drill";
  if (SWITCHING_SKILLS.has(skillId)) return "switching_framework";
  return "spot_drill";
}

interface DrillUrlInput {
  mode: SessionMode;
  kickoff: string;
  drillTarget: string;
  drillDifficulty: DrillDifficulty;
  /** When set, this launch is part of Today's Drill — the user's local
   *  date (YYYY-MM-DD). Threaded through to the chat stream so the
   *  backend serves the shared pinned spot + treats the turn as
   *  cap-exempt + advances the streak on completion. */
  dailyChallengeDate?: string;
  /** Base path. Drill helpers point at `/drill/new` (the focused
   *  single-spot view); defaults there. */
  path?: string;
}

/** Single source of truth for assembling the drill-launch URL. */
function buildDrillUrl(input: DrillUrlInput): string {
  const params = new URLSearchParams({
    mode: input.mode,
    kickoff: input.kickoff,
    drill_target: input.drillTarget,
    drill_difficulty: input.drillDifficulty,
  });
  if (input.dailyChallengeDate) {
    params.set("daily_challenge_date", input.dailyChallengeDate);
  }
  return `${input.path ?? NEW_DRILL_PATH}?${params.toString()}`;
}

/**
 * URL for drilling a specific skill — used by SkillDetail's "Drill X"
 * CTA and TodayPanel's "currently working on" rows. Difficulty defaults
 * to `"default"` (LLM-adaptive); backend composes the addendum from
 * the target + difficulty automatically.
 */
export function drillSkillRoute(skill: Pick<SkillState, "skillId" | "name">): string {
  return buildDrillUrl({
    mode: modeForSkill(skill.skillId),
    kickoff: `Drill me on ${skill.name.toLowerCase()}.`,
    drillTarget: skill.skillId,
    drillDifficulty: "default",
  });
}

/**
 * URL for drilling a leak by its canonical skill id. Foundational difficulty
 * because the user is fixing a known weakness — rebuild reps first; the kickoff
 * + mode are manufactured from the id, so callers supply only the skill id.
 * Used by the leak report (via `drillLeakRoute`) and by the coach's structured
 * drill-launch choices (`ChoicesBlock`).
 */
export function drillLeakSkillRoute(recommendedSkill: string): string {
  return buildDrillUrl({
    mode: modeForSkill(recommendedSkill),
    kickoff: `Drill me on ${recommendedSkill.replace(/_/g, " ")}. I want to fix this leak.`,
    drillTarget: recommendedSkill,
    drillDifficulty: "foundational",
  });
}

/**
 * URL for drilling a leak the detector flagged. Delegates to
 * `drillLeakSkillRoute` — kept as the `LeakPattern`-shaped entry point.
 */
export function drillLeakRoute(pattern: LeakPattern): string {
  return drillLeakSkillRoute(pattern.recommendedSkill);
}

/**
 * Decide how a coach's `choices` option behaves: a plain string re-sends its
 * label; a `{label, drill_target}` with a canonical target launches a drill.
 * Fail-closed — an unknown `drill_target` degrades to a re-send (never a launch
 * link that the backend would 422), so worst-case LLM drift equals legacy
 * behavior. Pure + co-located with the route helpers it uses; rendered by
 * `ChoicesBlock` and unit-tested in chatRoute.test.ts.
 */
export type OptionRender =
  | { kind: "resend"; label: string }
  | { kind: "launch"; label: string; to: string };

export function classifyOption(option: string | DrillChoiceOption): OptionRender {
  if (typeof option === "string") return { kind: "resend", label: option };
  if (VALID_DRILL_TARGETS.has(option.drill_target)) {
    return {
      kind: "launch",
      label: option.label,
      to: drillLeakSkillRoute(option.drill_target),
    };
  }
  return { kind: "resend", label: option.label };
}

/**
 * URL for launching the server-recommended drill. Backend supplies
 * the kickoff message, target, and difficulty; we thread them through
 * and pick the appropriate mode from the target.
 *
 * Variety recommendations (cold-start fundamentals) fall through to
 * `spot_drill` since variety is currently only emitted by the
 * fundamentals fallback in spot-drill territory. If the recommendation
 * engine ever produces an archetype-variety recommendation, this would
 * need to thread mode through the response separately.
 */
export function drillFromRecommendation(drill: RecommendedDrill): string {
  return buildDrillUrl({
    mode: modeForSkill(drill.drillTarget),
    kickoff: drill.kickoffMessage,
    drillTarget: drill.drillTarget,
    drillDifficulty: drill.drillDifficulty,
  });
}

/**
 * URL for launching one rung of Today's Drill. `target`/`mode` come from
 * the daily-challenge state; `difficulty` is the rung being attempted;
 * `date` is the user's local date (must match the date we query the
 * challenge with). The backend serves the shared pinned spot for
 * (date, difficulty) and advances the streak when the ladder is done.
 */
export function dailyChallengeRoute(input: {
  date: string;
  difficulty: DrillDifficulty;
  drillTarget: string;
  skillName: string;
}): string {
  return buildDrillUrl({
    mode: modeForSkill(input.drillTarget),
    kickoff: `Today's Drill: ${input.skillName.toLowerCase()}. Give me the spot.`,
    drillTarget: input.drillTarget,
    drillDifficulty: input.difficulty,
    dailyChallengeDate: input.date,
  });
}

/** URL for resuming an existing conversation. */
export function resumeRoute(conversationId: string): string {
  return `/chat/${conversationId}`;
}

// ---------------------------------------------------------------------------
// Session-tracker coaching launches. These are CONVERSATIONAL (no constructed
// spot) so they land on /chat/new with a mode + kickoff. The backend injects
// [SESSION FACTS] for these modes; a debrief also carries `session_id` so the
// backend binds that session's facts + routes signals to session_insights.
// ---------------------------------------------------------------------------
function buildCoachingUrl(input: {
  mode: SessionMode;
  kickoff: string;
  sessionId?: string;
  stake?: string;
  location?: string;
}): string {
  const params = new URLSearchParams({ mode: input.mode, kickoff: input.kickoff });
  if (input.sessionId) params.set("session_id", input.sessionId);
  // Segment context — the backend scopes [SESSION FACTS] to this slice so the
  // coach reasons about (e.g.) just your 1/2 game, not your whole history.
  if (input.stake) params.set("session_stake", input.stake);
  if (input.location) params.set("session_location", input.location);
  return `/chat/new?${params.toString()}`;
}

/** A tracker segment the coaching should be scoped to (the active filter). */
export interface SessionContext {
  /** Human label, e.g. "1/2" or "Nagids Game". */
  label: string;
  stake?: string;
  location?: string;
}

/** Pre-session warmup — set an intention before you sit down. Optionally
 *  scoped to a segment (the coach warms you up for *that* game). */
export function warmupRoute(ctx?: SessionContext): string {
  return buildCoachingUrl({
    mode: "session_warmup",
    kickoff: ctx
      ? `I'm about to play my ${ctx.label} game. Warm me up and help me set my intention for it.`
      : "I'm about to play a session. Warm me up and help me set my intention.",
    stake: ctx?.stake,
    location: ctx?.location,
  });
}

/** Post-session debrief — bound to the session just logged. */
export function debriefRoute(sessionId: string): string {
  return buildCoachingUrl({
    mode: "session_debrief",
    kickoff: "I just finished this session. Let's debrief it.",
    sessionId,
  });
}

/** Open strategic consult on the player's results + bankroll. Optionally
 *  scoped to a segment (consult about just that stake/location). */
export function consultRoute(ctx?: SessionContext): string {
  return buildCoachingUrl({
    mode: "session_consult",
    kickoff: ctx
      ? `Let's talk about my ${ctx.label} results — bankroll, trends, and what to fix next.`
      : "Let's talk about my results — bankroll, trends, and what to fix next.",
    stake: ctx?.stake,
    location: ctx?.location,
  });
}

/** Hand Analyzer — grade a saved built hand. Conversational (no constructed
 *  spot); the backend injects [HAND REPLAY] from `hand_id` and the coach grades
 *  the line. Goes through DrillLaunchLink so a walled free user hits the paywall
 *  here (building the hand was free; the coach grade is the paid product). */
export function handAnalysisRoute(handId: string): string {
  const params = new URLSearchParams({
    mode: "hand_analysis",
    hand_id: handId,
    kickoff: "Grade this hand for me — walk my line street by street.",
  });
  return `/chat/new?${params.toString()}`;
}
